import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "./api";

const StoreContext = createContext(null);

export function StoreProvider({ children }) {
  const [cart, setCart] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("dorae_cart") || "[]");
    } catch {
      return [];
    }
  });
  const [categories, setCategories] = useState([]);
  const [adminToken, setAdminToken] = useState(() => localStorage.getItem("dorae_admin_token"));
  const [seenOrders, setSeenOrders] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("dorae_seen_orders") || "[]");
    } catch {
      return [];
    }
  });
  const [ordersUpdated, setOrdersUpdated] = useState(0);

  useEffect(() => {
    localStorage.setItem("dorae_seen_orders", JSON.stringify(seenOrders));
  }, [seenOrders]);

  // Poll for order status updates for the saved customer email
  useEffect(() => {
    const em = localStorage.getItem("dorae_customer_email");
    if (!em) return;
    const check = async () => {
      try {
        const { data } = await api.get("/my-orders", { params: { email: em } });
        const unseen = data.orders.filter((o) => !seenOrders.includes(`${o.id}:${o.status}`)).length;
        setOrdersUpdated(unseen);
      } catch {}
    };
    check();
    const interval = setInterval(check, 20000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seenOrders]);

  const markOrdersSeen = (keys) => {
    setSeenOrders((prev) => {
      const set = new Set([...prev, ...keys]);
      return [...set];
    });
    setOrdersUpdated(0);
  };

  useEffect(() => {
    localStorage.setItem("dorae_cart", JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    if (adminToken) localStorage.setItem("dorae_admin_token", adminToken);
    else localStorage.removeItem("dorae_admin_token");
  }, [adminToken]);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data.categories)).catch(() => {});
  }, []);

  const addToCart = (product, qty = 1) => {
    setCart((prev) => {
      const existing = prev.find((i) => i.product_id === product.id);
      if (existing) {
        return prev.map((i) => (i.product_id === product.id ? { ...i, quantity: i.quantity + qty } : i));
      }
      return [...prev, { product_id: product.id, name: product.name, price: product.price, image_url: product.image_url, quantity: qty }];
    });
  };

  const updateQty = (product_id, delta) => {
    setCart((prev) =>
      prev
        .map((i) => (i.product_id === product_id ? { ...i, quantity: Math.max(0, i.quantity + delta) } : i))
        .filter((i) => i.quantity > 0)
    );
  };

  const removeFromCart = (product_id) => setCart((prev) => prev.filter((i) => i.product_id !== product_id));
  const clearCart = () => setCart([]);

  const cartCount = useMemo(() => cart.reduce((s, i) => s + i.quantity, 0), [cart]);
  const cartTotal = useMemo(() => cart.reduce((s, i) => s + i.price * i.quantity, 0), [cart]);

  const [customer, setCustomer] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("dorae_customer") || "null");
    } catch {
      return null;
    }
  });
  const [customerToken, setCustomerToken] = useState(() => localStorage.getItem("dorae_customer_token"));
  const [welcomed, setWelcomed] = useState(() => localStorage.getItem("dorae_welcomed") === "1");

  useEffect(() => {
    if (customer) localStorage.setItem("dorae_customer", JSON.stringify(customer));
    else localStorage.removeItem("dorae_customer");
  }, [customer]);

  useEffect(() => {
    if (customerToken) localStorage.setItem("dorae_customer_token", customerToken);
    else localStorage.removeItem("dorae_customer_token");
  }, [customerToken]);

  const loginCustomer = (user, token) => {
    setCustomer(user);
    setCustomerToken(token);
    if (user?.email) localStorage.setItem("dorae_customer_email", user.email.toLowerCase());
  };

  const logoutCustomer = () => {
    setCustomer(null);
    setCustomerToken(null);
  };

  const markWelcomed = () => {
    localStorage.setItem("dorae_welcomed", "1");
    setWelcomed(true);
  };

  const value = { cart, addToCart, updateQty, removeFromCart, clearCart, cartCount, cartTotal, categories, adminToken, setAdminToken, seenOrders, markOrdersSeen, ordersUpdated, customer, customerToken, loginCustomer, logoutCustomer, welcomed, markWelcomed };
  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore() {
  return useContext(StoreContext);
}
